import { useEffect, useMemo, ReactElement } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';

import Input from '~/ui/components/inputs/Input';
import Textarea from '~/ui/components/inputs/Textarea';
import Select from '~/ui/components/inputs/Select';
import SelectVirtualized from '~/ui/components/inputs/SelectVirtualized';
import DatePicker from '~/ui/components/inputs/DatePicker';
import RadioGroup from '~/ui/components/inputs/RadioGroup';

import countryOptions from '~/ui/constants/countryOptions';
import stateOptions from '~/ui/constants/stateOptions';
import genderOptions from '~/ui/constants/genderOptions';
import ethnicityOptions from '~/ui/constants/ethnicityOptions';
import homeHealthCareOptions from '~/ui/constants/homeHealthCareOptions';
import styles from './PatientForm.module.scss';
import PhoneInputsForm from './PhoneInputsForm';
import PhoneFlagInput from '~/ui/components/inputs/PhoneFlag';
import usePatientEnrollmentOptions from '~/ui/components/providers/PatientEnrollmentProvider/usePatientEnrollment';
import {
  insurancePlanOptions,
  insuranceTypeOptions,
  planTypeOptions,
} from '~/ui/components/reusable/InsuranceForm/constants';
import { IPatientCreateFormValues } from '../../types';
import Checkbox from '~/ui/components/inputs/Checkbox';

const PatientForm = (): ReactElement => {
  const { programs, managers, insuranceSelectors } = usePatientEnrollmentOptions();

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IPatientCreateFormValues>();

  const programOptions = useMemo(
    () =>
      programs.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [programs.length],
  );

  const managerOptions = managers.map(item => ({
    value: item.id,
    label: item.name,
  }));

  const isHomeHealthCare = watch('isHomeHealthCare');

  const homeHealthcareFields = useFieldArray<
    IPatientCreateFormValues,
    'homeHealthcarePeople',
    'id'
  >({
    control,
    name: 'homeHealthcarePeople',
  });

  useEffect(() => {
    if (Number(isHomeHealthCare) === 0) {
      homeHealthcareFields.remove();
    }
  }, [isHomeHealthCare]);

  const isAuthorizationRequired = watch('insurance.isAuthorizationRequired');

  const insuranceNameOptions = useMemo(
    () =>
      insuranceSelectors.names.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [insuranceSelectors],
  );

  useEffect(() => {
    // Reset "authorizationStartDate" and "authorizationEndDate" fields when uncheck the "isAuthorizationRequired" checkbox
    if (!isAuthorizationRequired) {
      setValue('insurance.authorizationStartDate', null);
      setValue('insurance.authorizationEndDate', null);
    }
  }, [isAuthorizationRequired]);

  return (
    <div className={styles.patientEnrollment}>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <SelectVirtualized
            name="programId"
            control={control}
            errors={errors}
            options={programOptions}
            label="Program"
            placeholder="Select"
            required
          />
        </Grid>
        <Grid item xs={4}>
          <SelectVirtualized
            name="healthCareManagerIds"
            control={control}
            errors={errors}
            options={managerOptions}
            isMulti
            label="Healthcare manager"
            required
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="subjectId"
            register={register}
            errors={errors}
            label="Patient ID"
            required
            placeholder="Patient ID"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="firstName"
            register={register}
            errors={errors}
            label="First Name"
            required
            placeholder="First Name"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="middleName"
            register={register}
            errors={errors}
            label="Middle Name"
            placeholder="Middle Name"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="lastName"
            register={register}
            errors={errors}
            label="Last Name"
            required
            placeholder="Last Name"
          />
        </Grid>
        <Grid item xs={4}>
          <PhoneFlagInput
            name="phoneNumber"
            control={control}
            errors={errors}
            label="Phone *"
            required
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            name="dateOfBirth"
            maxDate={new Date()}
            control={control}
            errors={errors}
            label="DOB"
            required
            placeholder=".. /.. /.."
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            name="ethnicity"
            control={control}
            errors={errors}
            options={ethnicityOptions}
            label="Ethnicity"
            placeholder="Ethnicity"
          />
        </Grid>
        <Grid item xs={4}>
          <RadioGroup
            name="isHomeHealthCare"
            control={control}
            errors={errors}
            options={homeHealthCareOptions}
            label="Home Healthcare"
          />
        </Grid>
        <Grid item xs>
          <RadioGroup
            name="gender"
            control={control}
            errors={errors}
            options={genderOptions}
            label="Gender"
          />
        </Grid>
        {Number(isHomeHealthCare) === 1 && (
          <Grid item xs={12}>
            <PhoneInputsForm
              control={control}
              name="homeHealthcarePeople"
              errors={errors}
              register={register}
              setValue={setValue}
            />
          </Grid>
        )}
      </Grid>

      <div className={styles.title}>
        LOCATION
        <div className={styles.divider} />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Select
            name="country"
            control={control}
            errors={errors}
            options={countryOptions}
            label="Country"
            required
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            name="state"
            control={control}
            errors={errors}
            options={stateOptions}
            label="State"
            required
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="city"
            register={register}
            errors={errors}
            label="City"
            required
            placeholder="City"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="address"
            register={register}
            errors={errors}
            label="Address"
            required
            placeholder="Address"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="zip"
            register={register}
            errors={errors}
            label="Zip"
            required
            placeholder="Zip code"
          />
        </Grid>
      </Grid>

      <div className={styles.title}>
        Insurance
        <div className={styles.divider} />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Select
            name="insurance.nameId"
            control={control}
            errors={errors}
            options={insuranceNameOptions}
            label="Name"
            required
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="insurance.groupId"
            register={register}
            errors={errors}
            label="Group Id"
            placeholder="Group ID"
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            name="insurance.policyNumber"
            register={register}
            errors={errors}
            label="Policy Number"
            placeholder="Policy Number"
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            name="insurance.type"
            control={control}
            errors={errors}
            options={insuranceTypeOptions}
            label="Insurance Type"
            required
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            name="insurance.plan"
            control={control}
            errors={errors}
            options={insurancePlanOptions}
            label="Insurance Plan"
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            name="insurance.planType"
            control={control}
            errors={errors}
            options={planTypeOptions}
            label="Plan Type"
            placeholder="Select"
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            name="insurance.expiresAt"
            control={control}
            errors={errors}
            label="Expiry Date"
            placeholder=".. /.. /.."
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Checkbox
            name="insurance.isAuthorizationRequired"
            control={control}
            errors={errors}
            label="Authorization Required?"
          />
        </Grid>
        {isAuthorizationRequired && (
          <>
            <Grid item xs>
              <DatePicker
                placeholder=".. /.. /.."
                label="Authorization From"
                required
                name="insurance.authorizationStartDate"
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid item xs>
              <DatePicker
                placeholder=".. /.. /.."
                label="Authorization Valid Through"
                required
                name="insurance.authorizationEndDate"
                control={control}
                errors={errors}
              />
            </Grid>
          </>
        )}
      </Grid>

      <div className={styles.title}>
        COMMENT
        <div className={styles.divider} />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Textarea name="comment" register={register} errors={errors} label="Comment" />
        </Grid>
      </Grid>
    </div>
  );
};

export default PatientForm;
